import { forwardRef } from 'react';

import { Accordion as BaseAccordion } from '@base-ui/react/accordion';
import { cn } from 'components/utils';

import {
  accordionActionsRecipe,
  accordionDescriptionRecipe,
  accordionGlyphRecipe,
  accordionHeaderRecipe,
  accordionIndicatorRecipe,
  accordionItemRecipe,
  accordionLeftSlotRecipe,
  accordionMetaRowRecipe,
  accordionPanelContentRecipe,
  accordionPanelRecipe,
  accordionRootRecipe,
  accordionTitleRecipe,
  accordionTitlesRecipe,
  accordionRightSlotRecipe,
  accordionTriggerRecipe,
} from './Accordion.recipe';
import { AccordionLayoutContext, useAccordionLayout } from './context';

import type { AccordionItemProps, AccordionProps } from './types';

/**
 * The indicator glyphs, transcribed from the set's own icon instances in a 16px box.
 *
 * Measured off the instances on 2026-09-23 rather than borrowed: each Figma glyph is a FILLED
 * outline 1.2px thick with round ends, which is a 1.2px round-capped stroke along the centreline, so
 * every path below is the outline's bounding box pulled in by 0.6px on each side.
 *
 *   `CaretRight`  outline x 4.9-11.1, y 2.4-13.6    ->  M5.5 3 L10.5 8 L5.5 13
 *   `CaretDown`   outline x 2.4-13.6, y 4.9-11.1    ->  M3 5.5 L8 10.5 L13 5.5
 *   `Plus`        outline 1.9-14.1 on both axes     ->  arms from 2.5 to 13.5
 *   `X`           outline 2.9-13.1 on both axes     ->  diagonals from 3.5 to 12.5
 *
 * The previous transcription got the Plus wrong in a way that was easy to see and hard to measure:
 * its arms ran 3.5-12.5, so the closed stacked glyph was 9px across where the set draws 11 — smaller
 * than the X it swaps with, when the set draws the Plus as the larger of the two. The carets sat
 * 0.5px off the set's centreline as well.
 *
 * Drawn here rather than imported from `icons` for the reason the previous revision gave and which
 * still holds: `src/icons` is MUI-backed (`createSvgIcon`, `createMaterialIcon`), so importing from
 * it would pull MUI sizing and theming into a Design System 2.0 primitive that has neither. HNS-262
 * will likely replace these and `Select`'s copy with a library import; until it lands, neither
 * component owns the other's icon.
 *
 * Stroke weight is 1.2px, the set's own. An earlier revision drew 1.5px to match `Select`'s caret and
 * recorded the mismatch as a gap; the drawing is the spec for this component, so the weight now
 * follows it. `Select` still draws its caret at 1.5px, and the two carets are now one notch apart —
 * that belongs to `Select`'s port rather than this one.
 *
 * ── Two glyphs drawn, four reached by rotation ───────────────────────────────────────────────────
 *
 * The set draws four cells; the code draws the two CLOSED glyphs and turns them into the open ones.
 * `CaretRight` rotated 90deg clockwise about (8, 8) lands on `CaretDown` point for point. `Plus`
 * rotated 45deg alone would reach 8 ± 3.889, a 7.78px X where the set draws 9×9, so it also scales
 * by 9√2 / 11 ≈ 1.1571, and both endpoints then land on the drawing. The transform is on a `<g>`
 * and its classes are `accordionGlyphRecipe`'s, on the panel's clock.
 *
 * `vectorEffect="non-scaling-stroke"` on every path, because the Plus is SCALED on its way to the X
 * and the stroke must stay the set's 1.2px through it. The property is not inherited, so it goes on
 * each path rather than the group. It also ignores the `<svg>`'s own viewBox scaling, which is
 * harmless while the box is a fixed 16px but means a larger rendering would keep a 1.2 screen-px
 * stroke.
 *
 * `aria-hidden` and `focusable="false"` on every one: the open state is announced by `aria-expanded`
 * on the trigger, so a second announcement here would be noise, and IE-era SVGs are tab stops
 * without the second attribute.
 */
const glyphProps = {
  'aria-hidden': true,
  fill: 'none',
  focusable: 'false',
  height: '16',
  viewBox: '0 0 16 16',
  width: '16',
} as const;

const strokeProps = {
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  strokeWidth: '1.2',
  vectorEffect: 'non-scaling-stroke',
} as const;

/**
 * Open or closed is decided by CSS, reading the trigger's `data-panel-open` through
 * `group/accordion-trigger`.
 *
 * A selector rather than a branch on Base UI's item state, and the reason is worth recording
 * because the obvious alternative looks cleaner: `Accordion.Trigger` does accept a render-function
 * child, so `{({ open }) => …}` would work. It would also make the whole trigger's children a
 * function, which puts the eyebrow row, the title block and both slots inside a closure that
 * re-runs on every state change — to turn one `<g>`.
 */

/** `CaretRight` closed, turning 90deg to `CaretDown` open — the disclosure convention. */
const InlineIndicatorIcon = () => (
  <svg {...glyphProps}>
    <g className={accordionGlyphRecipe({ layout: 'inline' })} data-part="glyph">
      <path d="M5.5 3L10.5 8L5.5 13" {...strokeProps} />
    </g>
  </svg>
);

/** `Plus` closed, turning 45deg and scaling to the drawn `X` open — the set's stacked pair. */
const StackedIndicatorIcon = () => (
  <svg {...glyphProps}>
    <g className={accordionGlyphRecipe({ layout: 'stacked' })} data-part="glyph">
      <path d="M2.5 8H13.5" {...strokeProps} />
      <path d="M8 2.5V13.5" {...strokeProps} />
    </g>
  </svg>
);

/**
 * Design System 2.0 accordion — a list of titled sections that expand and collapse.
 *
 * Ported from Figma 1447:135303 on 2026-09-18. Its surface and layout axes are public props; state,
 * open and disabled are supplied by the browser and Base UI. See `types.ts` for the public contract
 * and `Accordion.recipe.ts` for where each visual state is drawn.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer,
 * so the same markup is correct in both themes, and `.ds-accordion` needs no dark rule at all to
 * achieve it.
 *
 * ── What this component is NOT ────────────────────────────────────────────────────────────────
 *
 * It is not a tab set, and the difference is keyboard behaviour rather than appearance. Arrow keys
 * do nothing here: every row is an ordinary tab stop, and moving between them is Tab. That is the
 * accordion pattern, and it is also what Base UI 1.7.0 now implements — the APG guidance that
 * once put roving focus on an accordion was withdrawn, and the library's `orientation` and
 * `loopFocus` props are deprecated no-ops as a result. `types.ts` drops both rather than exposing
 * a knob that turns nothing.
 *
 * It is also not a way to hide content from the page while keeping it in the DOM. A collapsed
 * panel is UNMOUNTED, which is what `ExpandableBox` does and what callers of it expect: content
 * behind a closed section does not appear in a find-in-page, does not run its effects, and does
 * not make its network requests. Base UI can do the opposite through `keepMounted` and
 * `hiddenUntilFound`; both are dropped from this component's props, because a caller flipping one
 * would silently change all three of those properties for everything inside.
 *
 * It is not a row with two actions. The trigger is one `<button>` covering the whole row, which is
 * what makes the row clickable, and that forecloses a second control inside it — see `rightSlot` in
 * `types.ts`.
 *
 * ── Single or multiple open ───────────────────────────────────────────────────────────────────
 *
 * `multiple` is the caller's choice and is forwarded untouched, because a headless accordion
 * supports both at no cost. The default is Base UI's: one open section at a time. Note what single
 * mode does NOT do — there is no "one must stay open" behaviour, so pressing the open row's
 * trigger collapses it and leaves the list with nothing open. That is the pattern's normal
 * behaviour rather than an omission; a caller needing a section always open owns `value`.
 */
export const Accordion = ({
  children,
  className,
  iconPlacement = 'end',
  layout = 'inline',
  surface = 'filled',
  ...rest
}: AccordionProps) => (
  /**
   * `data-icon-placement`, `data-layout` and `data-surface` are read by selectors in the recipes
   * and in `design-system.css` rather than by any code here, which is what keeps three purely
   * visual choices out of React. They are set unconditionally so a test can read any of them for
   * either value, not only for the non-default one.
   *
   * `layout` and `surface` ALSO travel through context, because two of the parts need them to pick
   * a cva variant rather than to match a selector — the title's type level and the trigger's
   * insets are different class lists, not different values of one custom property, and a
   * `group-data` selector cannot swap a class list. The attribute and the context always agree;
   * the attribute is the one a test or a parent stylesheet should read.
   */
  <AccordionLayoutContext.Provider value={{ layout, surface }}>
    <BaseAccordion.Root
      className={cn(accordionRootRecipe({ surface }), className)}
      data-icon-placement={iconPlacement}
      data-layout={layout}
      data-surface={surface}
      {...rest}
    >
      {children}
    </BaseAccordion.Root>
  </AccordionLayoutContext.Provider>
);

/**
 * One titled section.
 *
 * A separate exported component rather than a `sections` array prop, so the content between the
 * tags is ordinary JSX — the whole requirement is "arbitrary content", and an array of render
 * functions is a worse way to write arbitrary content than children are.
 *
 * The parts are assembled here rather than left to the caller because their relationship is
 * what makes the thing accessible: the trigger is a real `<button>` inside Base UI's `<h3>`, so
 * the section appears in the document's heading outline and a screen reader can jump to it; the
 * button carries `aria-expanded`, and `aria-controls` pointing at the panel WHILE OPEN — Base UI
 * omits it when closed, since the panel it would name is not in the DOM to be pointed at; and the
 * panel is a `role="region"` labelled by the trigger. None of that is something a caller should
 * have to reassemble correctly.
 *
 * `data-part` names each element so a parent can target it and a test can assert on it without
 * depending on a styling class, the same convention the field pair uses for its box.
 */
export const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  (
    {
      children,
      className,
      description,
      leftSlot,
      panelClassName,
      rightSlot,
      showIndicator = true,
      title,
      triggerClassName,
      ...rest
    },
    ref
  ) => {
    const { layout, surface } = useAccordionLayout();

    const indicator = showIndicator ? (
      <span className={accordionIndicatorRecipe({ layout })} data-part="indicator">
        {layout === 'stacked' ? <StackedIndicatorIcon /> : <InlineIndicatorIcon />}
      </span>
    ) : null;

    const titles = (
      <span className={accordionTitlesRecipe()}>
        <span className={accordionTitleRecipe({ layout })} data-part="title">
          {title}
        </span>

        {description ? (
          <span className={accordionDescriptionRecipe()} data-part="description">
            {description}
          </span>
        ) : null}
      </span>
    );

    return (
      <BaseAccordion.Item
        ref={ref}
        className={cn(accordionItemRecipe({ layout, surface }), className)}
        data-part="item"
        {...rest}
      >
        <BaseAccordion.Header className={accordionHeaderRecipe()} data-part="header">
          <BaseAccordion.Trigger
            className={cn(accordionTriggerRecipe({ layout, surface }), triggerClassName)}
            data-part="trigger"
          >
            {layout === 'stacked' ? (
              <>
                {/* The eyebrow. The set expects `leftSlot` to be filled here — see `types.ts`. */}
                <span className={accordionMetaRowRecipe()} data-part="meta-row">
                  {leftSlot ? (
                    <span className={accordionLeftSlotRecipe()} data-part="left-slot">
                      {leftSlot}
                    </span>
                  ) : null}

                  <span className={accordionActionsRecipe()} data-part="actions">
                    {rightSlot ? (
                      <span className={accordionRightSlotRecipe()} data-part="right-slot">
                        {rightSlot}
                      </span>
                    ) : null}

                    {indicator}
                  </span>
                </span>

                {titles}
              </>
            ) : (
              <>
                {indicator}

                {leftSlot ? (
                  <span className={accordionLeftSlotRecipe()} data-part="left-slot">
                    {leftSlot}
                  </span>
                ) : null}

                {titles}

                {rightSlot ? (
                  <span className={accordionRightSlotRecipe()} data-part="right-slot">
                    {rightSlot}
                  </span>
                ) : null}
              </>
            )}
          </BaseAccordion.Trigger>
        </BaseAccordion.Header>

        {/* The padding lives on the inner wrapper, not on the panel — see `accordionPanelRecipe`. */}
        <BaseAccordion.Panel className={accordionPanelRecipe()} data-part="panel">
          <div
            className={cn(accordionPanelContentRecipe({ layout, surface }), panelClassName)}
            data-part="panel-content"
          >
            {children}
          </div>
        </BaseAccordion.Panel>
      </BaseAccordion.Item>
    );
  }
);
